import { Button, Radio, Switch, TextLink, Tooltip, Typography } from '@neo4j-ndl/react';
import { BookOpenIconOutline, CheckCircleIconSolid, ExclamationTriangleIconOutline } from '@neo4j-ndl/react/icons';
import { TIER, useActiveOrg, useActiveProject } from '@nx/state';
import type { Organization } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { useNavigate } from 'react-router';

import { MINIMUM_GDS_PLUGIN_VECTOR_MEMORY } from '../constants';
import { gbStringToInt, tierDisplayName } from '../entities/helpers';
import type { InstanceFormData } from '../entities/model';
import { BaseInstanceCreateSection, BaseSectionItemsWrapper } from './base-section';

const gdsPluginDisabledReason = (data: InstanceFormData) => {
  if (isNullish(data.size)) {
    return 'You must select a size before enabling this feature';
  }

  if (![TIER.PROFESSIONAL].includes(data.tier)) {
    return 'Graph Analytics Plugin is not available for this tier';
  }

  if (data.version !== '5') {
    return 'Graph Analytics Plugin is not available for the selected version';
  }

  if (gbStringToInt(data.size.memory) < MINIMUM_GDS_PLUGIN_VECTOR_MEMORY) {
    return '4GB memory or greater is required for the Graph Analytics Plugin configuration';
  }

  return null;
};

export const getValidGdsPluginValue = (data: InstanceFormData) => {
  if (!gdsPluginDisabledReason(data)) {
    return data.gdsPlugin;
  }

  if ([TIER.GDS, TIER.AURA_DSE].includes(data.tier)) {
    return true;
  }

  return false;
};

const OptionDetails = ({ hasGdsPlugin, org }: { hasGdsPlugin: boolean; org: Organization }) => {
  const navigate = useNavigate();

  if (!org.gdsSessions.enabled) {
    return (
      <div className="flex flex-row items-center gap-1">
        <ExclamationTriangleIconOutline strokeWidth="2" className="stroke-palette-warning-icon size-4" />
        <Typography variant="body-small" className="text-warning-text">
          <b>Graph Analytics Serverless</b> has been disabled by an organization admin. Go to{' '}
          <TextLink
            className="cursor-pointer no-underline"
            htmlAttributes={{
              onClick: () => {
                void navigate(`/org/${org.id}/settings`);
              },
            }}
          >
            Settings
          </TextLink>{' '}
          to enable the feature.
        </Typography>
      </div>
    );
  }
  if (hasGdsPlugin) {
    return (
      <div className="flex flex-row items-center gap-1">
        <ExclamationTriangleIconOutline strokeWidth="2" className="stroke-palette-warning-icon size-4" />
        <Typography variant="body-small" className="text-warning-text">
          <b>Graph Analytics Serverless</b> will not be available if the plugin is enabled.
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center gap-1">
      <CheckCircleIconSolid className="text-success-icon size-4" />
      <Typography variant="body-small" className="text-success-text">
        Recommended for dynamic scaling and minimal infrastructure overhead.
      </Typography>
    </div>
  );
};

type GdsPluginCreateProps = {
  data: InstanceFormData;
  onDataChange: (data: Partial<InstanceFormData>) => void;
};

export const GraphAnalyticsSection = ({ data, onDataChange }: GdsPluginCreateProps) => {
  const org = useActiveOrg();
  const project = useActiveProject();
  const handleGdsPluginChange = (value: boolean) => {
    onDataChange({ gdsPlugin: value });
  };

  const pluginDisabledReason = gdsPluginDisabledReason(data);
  const isDisabled = isNotNullish(pluginDisabledReason);
  const isServerlessEnabled = org.gdsSessions.enabled;
  const isProfessionalTier = data.tier === TIER.PROFESSIONAL;

  return (
    <BaseInstanceCreateSection
      title={
        <div className="flex items-center justify-between gap-2">
          Graph Analytics
          <Button
            size="small"
            color="neutral"
            fill="outlined"
            className="flex shrink-0 items-center gap-2"
            as="a"
            htmlAttributes={{ href: 'https://neo4j.com/docs/aura/graph-analytics/' }}
          >
            <BookOpenIconOutline className="size-4 shrink-0" />
            Docs
          </Button>
        </div>
      }
    >
      <BaseSectionItemsWrapper>
        <div className="flex w-full grow items-center justify-between">
          <div className="flex flex-col gap-1">
            <Typography variant="subheading-small">
              Serverless{' '}
              <span className="text-neutral-text-weaker">
                {isServerlessEnabled ? '(recommended)' : '(unavailable)'}
              </span>
            </Typography>
            <Typography variant="body-small" as="div" className="text-neutral-text-weak">
              Configure the instance to run graph algorithms in an on-demand ephemeral compute environment.
            </Typography>
            <OptionDetails hasGdsPlugin={data.gdsPlugin} org={org} />
          </div>
          <div>
            {isProfessionalTier ? (
              <>
                {isServerlessEnabled && (
                  <Radio
                    isChecked={!data.gdsPlugin}
                    onChange={() => handleGdsPluginChange(false)}
                    ariaLabel="Enable Graph Analytics Serverless"
                    htmlAttributes={{
                      'data-testid': 'radio-button-gds-sessions',
                    }}
                  />
                )}
              </>
            ) : (
              <>
                {isServerlessEnabled ? (
                  <Radio
                    isChecked
                    ariaLabel="Enable Graph Analytics Serverless"
                    htmlAttributes={{
                      'data-testid': 'radio-button-gds-sessions',
                    }}
                  />
                ) : (
                  <Tooltip type="simple" isPortaled={false} isDisabled={false}>
                    <Tooltip.Trigger>
                      <Radio
                        isChecked={false}
                        isDisabled
                        ariaLabel="Enable Graph Analytics Serverless"
                        htmlAttributes={{
                          'data-testid': 'radio-button-gds-sessions',
                        }}
                      />
                    </Tooltip.Trigger>
                    <Tooltip.Content>
                      Graph Analytics Serverless has been disabled by an organization admin.
                    </Tooltip.Content>
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex w-full grow items-center justify-between">
          <div className="flex flex-col gap-1">
            <Typography variant="subheading-small">
              Plugin{' '}
              {!isProfessionalTier && (
                <span className="text-neutral-text-weaker">
                  (unavailable{data.tier === TIER.ENTERPRISE && ` on ${tierDisplayName(project, data.tier)}`})
                </span>
              )}
            </Typography>
            <Typography variant="body-small" as="div" className="text-neutral-text-weak">
              Enable the instance to run graph algorithms sharing memory and compute with the database.
            </Typography>
          </div>
          {!isProfessionalTier ? (
            <Tooltip type="simple" isPortaled={false} isDisabled={!isDisabled}>
              <Tooltip.Trigger>
                <Radio
                  isChecked={data.gdsPlugin}
                  onChange={() => handleGdsPluginChange(true)}
                  isDisabled={isDisabled}
                  ariaLabel="Enable Graph Analytics Plugin"
                  htmlAttributes={{
                    'data-testid': 'radio-button-gds-plugin',
                  }}
                />
              </Tooltip.Trigger>
              <Tooltip.Content>{pluginDisabledReason}</Tooltip.Content>
            </Tooltip>
          ) : (
            <>
              {isServerlessEnabled ? (
                <Tooltip type="simple" isPortaled={false} isDisabled={!isDisabled}>
                  <Tooltip.Trigger>
                    <Radio
                      isChecked={data.gdsPlugin}
                      onChange={() => handleGdsPluginChange(true)}
                      isDisabled={isDisabled}
                      ariaLabel="Enable Graph Analytics Plugin"
                      htmlAttributes={{
                        'data-testid': 'radio-button-gds-plugin',
                      }}
                    />
                  </Tooltip.Trigger>
                  <Tooltip.Content>{pluginDisabledReason}</Tooltip.Content>
                </Tooltip>
              ) : (
                <Tooltip type="simple" isPortaled={false} isDisabled={!isDisabled}>
                  <Tooltip.Trigger>
                    <Switch
                      htmlAttributes={{
                        'aria-label': 'Toggle to install Graph Analytics Plugin on instance',
                        'data-testid': 'gds-plugin-switch',
                      }}
                      ariaLabel="Toggle to install Graph Analytics Plugin on instance"
                      isChecked={data.gdsPlugin}
                      onChange={(e) => handleGdsPluginChange(e.target.checked)}
                      isDisabled={isDisabled}
                    />
                  </Tooltip.Trigger>
                  <Tooltip.Content>{pluginDisabledReason}</Tooltip.Content>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </BaseSectionItemsWrapper>
    </BaseInstanceCreateSection>
  );
};
